import {
  type CreateIntentRequest,
  paypalCheckoutChargeResult,
  paypalCheckoutCreateIntent,
} from "@infrastructure/payments";
import logger from "@logger";
import { type RefObject, useEffect, useRef } from "react";

const PAYPAL_SCRIPT_ID = "fitties-paypal-checkout-buttons";
const PAYPAL_SDK_BASE = "https://www.paypal.com/sdk/js";

let payPalSdkInflight: Promise<void> | null = null;

function formatPayPalClientError(e: unknown): string {
  if (e instanceof Error && e.message.trim()) {
    return e.message;
  }
  if (typeof e === "string" && e.trim()) {
    return e;
  }
  if (e && typeof e === "object") {
    const o = e as Record<string, unknown>;
    for (const key of ["message", "err", "error", "details", "debug_id"] as const) {
      const v = o[key];
      if (typeof v === "string" && v.trim()) {
        return v;
      }
    }
    try {
      return JSON.stringify(e);
    } catch {
      return String(e);
    }
  }
  return String(e);
}

function ensurePayPalButtonsSdk(clientId: string, currencyUpper: string, shouldContinue: () => boolean): Promise<void> {
  if (window.paypal?.Buttons) {
    return Promise.resolve();
  }
  if (payPalSdkInflight) {
    return payPalSdkInflight;
  }
  payPalSdkInflight = new Promise((resolve, reject) => {
    const clearInflight = () => {
      payPalSdkInflight = null;
    };
    const existing = document.getElementById(PAYPAL_SCRIPT_ID);
    if (existing) {
      const done = () => {
        clearInflight();
        if (!shouldContinue()) {
          resolve();
          return;
        }
        if (window.paypal?.Buttons) {
          resolve();
          return;
        }
        reject(new Error("PayPal SDK script present but Buttons unavailable."));
      };
      if (window.paypal?.Buttons) {
        done();
        return;
      }
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener(
        "error",
        () => {
          clearInflight();
          reject(new Error("PayPal SDK script failed to load."));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = PAYPAL_SCRIPT_ID;
    const params = new URLSearchParams({
      "client-id": clientId,
      components: "buttons",
      currency: currencyUpper,
      intent: "capture",
    });
    script.src = `${PAYPAL_SDK_BASE}?${params.toString()}`;
    script.async = true;
    script.onload = () => {
      clearInflight();
      if (!shouldContinue()) {
        resolve();
        return;
      }
      resolve();
    };
    script.onerror = () => {
      clearInflight();
      if (!shouldContinue()) {
        resolve();
        return;
      }
      reject(new Error("PayPal SDK script failed to load (network, blocked host, or invalid client-id)."));
    };
    document.body.appendChild(script);
  });
  return payPalSdkInflight;
}

function waitDoubleAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

async function mountPayPalButtonsInHost(options: {
  host: HTMLDivElement;
  clientId: string;
  currency: string;
  getAmountCentsRef: { current: () => number };
  metadataRef: { current: Record<string, string> };
  log: (line: string) => void;
  onChargeResult?: (result: Awaited<ReturnType<typeof paypalCheckoutChargeResult>>) => Promise<void> | void;
  onErrorBanner?: (message: string | null) => void;
  cancelled: () => boolean;
}): Promise<{ close: () => void } | null> {
  const { host, clientId, currency, getAmountCentsRef, metadataRef, log, onChargeResult, onErrorBanner, cancelled } =
    options;
  await ensurePayPalButtonsSdk(clientId, currency.toUpperCase(), () => !cancelled());
  if (cancelled()) {
    return null;
  }
  const paypal = window.paypal;
  if (!paypal?.Buttons) {
    onErrorBanner?.("PayPal SDK loaded without Buttons.");
    return null;
  }

  const createOrder = async (): Promise<string> => {
    const body: CreateIntentRequest = {
      amountCents: getAmountCentsRef.current(),
      currency,
      metadata: metadataRef.current,
    };
    const result = await paypalCheckoutCreateIntent(body);
    if (!result.success) {
      log(`create-intent failed: ${JSON.stringify(result)}`);
      throw new Error(result.message);
    }
    log(`create-intent ok orderId=${result.intentId}`);
    return result.intentId;
  };

  const onApprove = async (data: { orderID: string }) => {
    const result = await paypalCheckoutChargeResult(data.orderID);
    log(`charge-result: ${JSON.stringify(result)}`);
    await onChargeResult?.(result);
  };

  const onError = (err: unknown) => {
    log(`PayPal onError: ${formatPayPalClientError(err)}`);
  };

  if (cancelled()) {
    return null;
  }
  host.replaceChildren();
  const buttons = paypal.Buttons({
    style: { layout: "vertical", shape: "rect" },
    createOrder,
    onApprove,
    onError,
  });
  const out = buttons.render(host);
  if (out !== undefined && typeof (out as Promise<void>).then === "function") {
    await (out as Promise<void>);
  }
  if (cancelled()) {
    buttons.close();
    return null;
  }
  return buttons;
}

async function executePayPalCheckoutMount(options: {
  hostRef: RefObject<HTMLDivElement | null>;
  clientId: string;
  currency: string;
  getAmountCentsRef: { current: () => number };
  metadataRef: { current: Record<string, string> };
  onLogRef: { current: ((line: string) => void) | undefined };
  onChargeResultRef: {
    current: ((result: Awaited<ReturnType<typeof paypalCheckoutChargeResult>>) => Promise<void> | void) | undefined;
  };
  onErrorBannerRef: { current: ((message: string | null) => void) | undefined };
  cancelled: () => boolean;
}): Promise<{ close: () => void } | null> {
  await waitDoubleAnimationFrame();
  if (options.cancelled()) {
    return null;
  }
  const host = options.hostRef.current;
  if (!host) {
    return null;
  }
  const log = (line: string) => {
    options.onLogRef.current?.(line);
  };
  return mountPayPalButtonsInHost({
    host,
    clientId: options.clientId,
    currency: options.currency,
    getAmountCentsRef: options.getAmountCentsRef,
    metadataRef: options.metadataRef,
    log,
    ...(options.onChargeResultRef.current ? { onChargeResult: options.onChargeResultRef.current } : {}),
    cancelled: options.cancelled,
    ...(options.onErrorBannerRef.current ? { onErrorBanner: options.onErrorBannerRef.current } : {}),
  });
}

type PayPalCheckoutBootDeps = {
  hostRef: RefObject<HTMLDivElement | null>;
  clientId: string;
  currency: string;
  getAmountCentsRef: { current: () => number };
  metadataRef: { current: Record<string, string> };
  cancelled: () => boolean;
  setButtonsInstance: (instance: { close: () => void } | null) => void;
  onLogRef: { current: ((line: string) => void) | undefined };
  onChargeResultRef: {
    current: ((result: Awaited<ReturnType<typeof paypalCheckoutChargeResult>>) => Promise<void> | void) | undefined;
  };
  onErrorBannerRef: { current: ((message: string | null) => void) | undefined };
};

async function runPayPalCheckoutBootEffect(deps: PayPalCheckoutBootDeps): Promise<void> {
  try {
    const btns = await executePayPalCheckoutMount({
      hostRef: deps.hostRef,
      clientId: deps.clientId,
      currency: deps.currency,
      getAmountCentsRef: deps.getAmountCentsRef,
      metadataRef: deps.metadataRef,
      onLogRef: deps.onLogRef,
      onChargeResultRef: deps.onChargeResultRef,
      onErrorBannerRef: deps.onErrorBannerRef,
      cancelled: deps.cancelled,
    });
    if (deps.cancelled()) {
      btns?.close();
      return;
    }
    deps.setButtonsInstance(btns ?? null);
  } catch (e: unknown) {
    deps.onErrorBannerRef.current?.(formatPayPalClientError(e));
    logger.error("PayPal checkout buttons:", e);
  }
}

export type PayPalCheckoutButtonsProps = {
  clientId: string;
  getAmountCents: () => number;
  currency?: string;
  metadata?: Record<string, string>;
  onLog?: (line: string) => void;
  onChargeResult?: (result: Awaited<ReturnType<typeof paypalCheckoutChargeResult>>) => Promise<void> | void;
  onErrorBanner?: (message: string | null) => void;
  containerClassName?: string;
  "data-testid"?: string;
};

export default function PayPalCheckoutButtons({
  clientId,
  getAmountCents,
  currency = "gbp",
  metadata = {},
  onLog,
  onChargeResult,
  onErrorBanner,
  containerClassName,
  "data-testid": dataTestId,
}: PayPalCheckoutButtonsProps) {
  "use no memo";

  const hostRef = useRef<HTMLDivElement>(null);
  const buttonsCleanupRef = useRef<{ close: () => void } | null>(null);
  const getAmountCentsRef = useRef(getAmountCents);
  const metadataRef = useRef(metadata);
  const onLogRef = useRef(onLog);
  const onChargeResultRef = useRef(onChargeResult);
  const onErrorBannerRef = useRef(onErrorBanner);
  getAmountCentsRef.current = getAmountCents;
  metadataRef.current = metadata;
  onLogRef.current = onLog;
  onChargeResultRef.current = onChargeResult;
  onErrorBannerRef.current = onErrorBanner;

  useEffect(() => {
    let cancelled = false;
    buttonsCleanupRef.current = null;

    void runPayPalCheckoutBootEffect({
      hostRef,
      clientId,
      currency,
      getAmountCentsRef,
      metadataRef,
      onLogRef,
      onChargeResultRef,
      onErrorBannerRef,
      cancelled: () => cancelled,
      setButtonsInstance: (instance) => {
        buttonsCleanupRef.current = instance;
      },
    });

    return () => {
      cancelled = true;
      const btns = buttonsCleanupRef.current;
      buttonsCleanupRef.current = null;
      try {
        btns?.close();
      } catch {
        /* PayPal SDK cleanup */
      }
      hostRef.current?.replaceChildren();
    };
  }, [clientId, currency]);

  return <div className={containerClassName} data-testid={dataTestId} ref={hostRef} />;
}
